import React, { useState, useEffect, useContext } from "react";
import { AppContext } from "../../App";
import "./UserTrips.css";
import { useNavigate } from 'react-router-dom';
import Navbar from '../layout/navbar';
import { ImageCollage } from '../imageCollage/ImageCollage';
import ShareTripModal from './ShareTripModal';
import UserTripsTutorialModal from "./UserTripsTutorialModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Utility: Detect if device is mobile
function isMobileDevice() {
  return /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);
}

const UserTrips = () => {
  const { userCtx } = useContext(AppContext);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ownership, setOwnership] = useState({}); // { [tripId]: true/false }
  const navigate = useNavigate();

  // --- Share modal state ---
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareTripId, setShareTripId] = useState(null);
  const [sharedUsers, setSharedUsers] = useState([]);

  // Tutorial modal state
  const [showTutorial, setShowTutorial] = useState(false);

  // Fetch trips
  useEffect(() => {
    const fetchTrips = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${BACKEND_URL}/newtrips`, {
          method: 'GET',
          credentials: 'include'
        });
        if (!response.ok) throw new Error("Failed to fetch trips");
        const data = await response.json();
        setTrips(data);

        // Check ownership for each trip
        const ownershipResults = {};
        await Promise.all(
          data.map(async (trip) => {
            try {
              const res = await fetch(`${BACKEND_URL}/check-userownership/${trip._id}`, {
                method: 'GET',
                credentials: 'include'
              });
              const result = await res.json();
              ownershipResults[trip._id] = !!result.isOwner;
            } catch {
              ownershipResults[trip._id] = false;
            }
          })
        );
        setOwnership(ownershipResults);
      } catch (error) {
        console.error("Error fetching trips:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTrips();
  }, [userCtx]);

  useEffect(() => {
    if (!localStorage.getItem("hideUserTripsTutorial")) {
      setShowTutorial(true);
    }
  }, []);

  // Handler for opening share modal
  const handleOpenShare = (trip) => {
    setShareTripId(trip._id);
   // setSharedUsers(loadSharedUsers(trip._id)); // Reset shared users
    setShareModalOpen(true);
  };

 

  // Handler for double-click on a trip panel
  const handleTripDoubleClick = (trip) => {
      // Navigate to trip details page and pass the trip object as state
      navigate(`/trip-details/${trip._id}`, { state: { trip } });
    };

// Delete trip handler
const handleDeleteTrip = async (tripId) => {
if (!window.confirm('Are you sure you want to delete this trip?')) return;
try {
  const response = await fetch(`${BACKEND_URL}/newtrips/${tripId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (response.ok) {
    setTrips((prev) => prev.filter((t) => t._id !== tripId));
  } else {
    alert('Failed to delete trip');
  }
} catch (e) {
  alert('Error deleting trip: ' + e.message);
}
};

// Handler for editing a trip
const handleEditTrip = (trip) => {
navigate('/createTrip', { state: { trip, editMode: true } });
};

// Handler for duplicating a trip
const handleDuplicateTrip = async (tripId) => {
  try {
    const response = await fetch(`${BACKEND_URL}/newtrips/${tripId}/duplicate`, {
      method: 'POST',
      credentials: 'include',
    });
    
    if (response.ok) {
      const duplicatedTrip = await response.json();
      // Add the new trip to the end of the trips list
      setTrips((prev) => [...prev, duplicatedTrip]);
      // Set ownership for the new trip
      setOwnership(prev => ({ ...prev, [duplicatedTrip._id]: true }));
    } else {
      const errorData = await response.json();
      alert(`Failed to duplicate trip: ${errorData.message || 'Unknown error'}`);
    }
  } catch (error) {
    console.error('Error duplicating trip:', error);
    alert('Error duplicating trip: ' + error.message);
  }
};



  return (
    <div className="user-trips-root">
      <Navbar />
      {loading ? (
        <h2 style={{ textAlign: "center", marginTop: "2em" }}>Loading ...</h2>
      ) : trips.length === 0 ? (
        <h2 style={{ textAlign: "center", marginTop: "2em" }}>No trips available.</h2>
      ) : (
        <div className="trips-grid">
          {trips.map((trip) => {
            const isOwner = ownership[trip._id] !== false; // default true until checked
            return (
              <div
                className="trip-panel"
                key={trip._id}
                onDoubleClick={isMobileDevice() ? undefined : (e) => { e.stopPropagation(); handleTripDoubleClick(trip); }}
                onClick={isMobileDevice() ? (e) => { e.stopPropagation(); handleTripDoubleClick(trip); } : undefined}
              >
                <div className="trip-panel-image" style={{ position: "relative" }}>
                  <ImageCollage countries={trip.countries} />
                  {!isOwner && (
                    <div className="trip-shared-ribbon">Shared</div>
                  )}
                </div>
                <div className="trip-panel-content">
                  <h3>{trip.tripName}</h3>
                  <div className="desc-type-row">
                    <div className="desc-cell">
                      <strong>Description:</strong>{" "}
                      {trip.description ? trip.description : <span className="placeholder">—</span>}
                    </div>
                    <div className="type-cell mobile-hide">
                      <strong>Type:</strong> {trip.type}
                    </div>
                  </div>
                  <div className="type-cell mobile-only">
                    <strong>Type:</strong> {trip.type}
                  </div>
                  <div className="countries-cities-list">
                    <strong>Locations:</strong>
                    {Array.isArray(trip.countries) && trip.countries.length > 0 && (
                      <ul>
                        {trip.countries.map((country, idx) => {
                          const cities = Array.isArray(trip.cities)
                            ? trip.cities
                                .filter(cityObj => cityObj.cityAndCountry && cityObj.cityAndCountry.endsWith(`, ${country}`))
                                .map(cityObj => cityObj.cityAndCountry.split(',')[0].trim())
                            : [];
                          return (
                            <li key={country}>
                              {country} -{" "}
                              <span style={{ fontStyle: "italic" }}>
                                {cities.length > 0 ? cities.join(", ") : <span className="placeholder">—</span>}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                  <div>
                    <strong>Dates:</strong>{" "}
                    {trip.fixedDates
                      ? (
                          trip.startDate && trip.endDate
                            ? `${new Date(trip.startDate).toLocaleDateString()} - ${new Date(trip.endDate).toLocaleDateString()}`
                            : <span className="placeholder">—</span>
                        )
                      : "N/A"
                    }
                  </div>
                </div>
                <button
                  className="trip-edit-btn"
                  title="Edit Trip"
                  onClick={e => { e.stopPropagation(); handleEditTrip(trip); }}
                  disabled={!isOwner}
                >
                  ✎
                </button>
                {/* --- Share Trip Button (small circle, between edit and delete) --- */}
                <button
                  className="trip-share-btn"
                  title="Share Trip"
                  onClick={e => {
                    e.stopPropagation();
                    handleOpenShare(trip);
                  }}
                  disabled={!isOwner}
                >
                  🔗
                </button>
                {/* --- Duplicate Trip Button --- */}
                <button
                  className="trip-duplicate-btn"
                  title="Duplicate Trip"
                  onClick={e => {
                    e.stopPropagation();
                    handleDuplicateTrip(trip._id);
                  }}
                >
                  ❐
                </button>
                <button
                  className="trip-delete-btn"
                  title="Delete Trip"
                  onClick={(e) => { e.stopPropagation(); handleDeleteTrip(trip._id); }}
                  disabled={!isOwner}
                >
                  🗑
                </button>
                <button
                  className="trip-ai-btn"
                  title="AI Insights"
                  onClick={e => {
                    e.stopPropagation();
                    const hasCities = Array.isArray(trip.cities) && trip.cities.length > 0;
                    const cityNames = hasCities
                      ? trip.cities.map(city =>
                          city.name
                        )
                      : trip.countries;
                    navigate('/trip-ai', {
                      state: {
                        tripId: trip._id,
                        tripName: trip.tripName,
                        cities: cityNames
                      }
                    });
                  }}
                  disabled={!isOwner ||!userCtx || !userCtx.layer || userCtx.layer < 2}
                >
                  <span role="img" aria-label="AI Insights">
                    🪄
                  </span>
                </button>
                <button
                  className="trip-map-btn"
                  title="Trip Planner"
                  onClick={e => {
                    e.stopPropagation();
                    handleTripDoubleClick(trip);
                  }}
                >
                  <span role="img" aria-label="Map">🗺️</span>
                </button>
              </div>
            );
          })}
        </div>
      )}
      {/* Floating Add Button */}
      <button
        className="fab-add-trip"
        title="Add New Trip"
        onClick={() => navigate('/createTrip')}
      >
        ＋
      </button>

      {/* Share Trip Modal */}
      {shareModalOpen && (
        <ShareTripModal
          key={shareTripId} // <--- add this line
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          tripId={shareTripId}
          sharedUsers={sharedUsers}
        />
      )}
      {showTutorial && (
        <UserTripsTutorialModal onClose={() => setShowTutorial(false)} />
      )}
    </div>
  );
};

export default UserTrips;

